import React, { Ref, useMemo } from "react";
import moment from "moment";
import { Bar } from "../../plugins/react-chartjs-2";
import config, { DataPoint } from "../../constants/charts";
import { defaultChartOptions } from "../../helpers/charts";
import { useMetricsState } from "../../context/metrics";

import { getChartConfig } from "../../helpers/getChartConfig";
import { useChartData } from "../../data/useChartData";
import { useAnalyticsState } from "../../context/analytics";
import { Resolution, roundTo } from "./Grouping";
import { Theme } from "@material-ui/core";
import { DateRange } from "../../api/agritech";
import { Field } from "../../api/fields";

function getGroupedDatasets(
  data: DataPoint[],
  selectedResolution: Resolution,
  differenceInWeeks: number
) {
  const groupedData = new Map<string, Map<number, DataPoint>>();
  let selectedRes = selectedResolution;
  //if selected resolution is 24 hours,  difference in days must be more than 1
  if (selectedRes === "24 hours") {
    const diffInDays = differenceInWeeks * 7;
    if (diffInDays <= 1) {
      selectedRes = "1 hour";
    }
  } else if (selectedRes === "15 mins") {
    //if selected resolution is 15 mins,  difference in weeks must be more than 2
    const diffInWeeks = differenceInWeeks;
    if (diffInWeeks >= 2) {
      selectedRes = "1 hour";
    }
  }

  let dateKeyFormat = "YYYY-MM-DD HH";
  if (selectedRes === "15 mins") {
    dateKeyFormat = "YYYY-MM-DD HH:mm";
  } else if (selectedRes === "1 hour") {
    dateKeyFormat = "YYYY-MM-DD HH";
  } else if (selectedRes === "24 hours") {
    dateKeyFormat = "YYYY-MM-DD";
  }

  // Group data based on date
  data.forEach((record) => {
    const date = moment(record.x).format(dateKeyFormat);
    const group = groupedData.get(date) ?? new Map<number, DataPoint>();
    group.set(record.x, {
      id: record.id,
      x: record.x,
      y: record.y > 0 ? record.y : 0,
    });
    groupedData.set(date, group);
  });

  // Sum data based on groups
  const groups = Array.from(groupedData.values());
  return groups.map((group) => {
    const firstX = [...group.keys()][0];
    const res = {
      x: roundTo(firstX, selectedResolution),
      y: 0,
    };
    group.forEach(({ y }) => {
      res.y += y;
    });
    return res;
  });
}

const ChartDefault = ({
  chart,
  fieldId,
  range,
  theme,
  chartEvents,
  selectedResolution,
  field,
}: {
  chart: { id: keyof typeof config; group: string };
  fieldId: number;
  field: Field;
  range: DateRange;
  selectedResolution: Resolution;
  theme: Theme;
  chartEvents: {
    chartRef: Ref<any>;
  };
}) => {
  const { chartData } = useChartData(chart.group, range, fieldId);
  const metrics = useMetricsState();

  // calculate diff in weeks/days to display correct information
  const {
    range: { start_date, end_date },
  } = useAnalyticsState();
  const calculateDifferenceInWeeks = () => {
    const msInWeek = 1000 * 60 * 60 * 24 * 7;
    const diffEpoch = end_date.valueOf() - start_date.valueOf();
    const differenceInWeeks = diffEpoch / msInWeek;
    return differenceInWeeks;
  };
  const differenceInWeeks = calculateDifferenceInWeeks();

  const chartConfig = useMemo(() => {
    var data = getChartConfig(chartData, {
      chartType: chart.id,
      theme,
      metrics,
      field,
    });

    data.datasets.forEach((item, i) => {
      if (item.label === "Rainfall") {
        // @ts-expect-error - changing type of item.data
        item.data = getGroupedDatasets(
          item.data as DataPoint[],
          selectedResolution,
          differenceInWeeks
        );
      }
    });
    return data;
  }, [chartData, theme, chart.id, metrics, selectedResolution]);

  const options = useMemo(
    () =>
      defaultChartOptions(
        config[chart.id].axis,
        config[chart.id].legend,
        range,
        chartEvents
      ),
    [chart.id, range]
  );

  return (
    // @ts-expect-error - data does not exist in props, apparently?
    <Bar ref={chartEvents.chartRef} data={chartConfig} options={options} />
  );
};

export default ChartDefault;
